import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
  }

  /**
   * Re-applies WishlistKing state whenever a section finishes (re-)loading —
   * notably after `section-hydration.js` hydrates the header on initial page
   * load, which would otherwise morph away the `wk-selected` class.
   * @param {Event} event
   */
  #handleSectionLoad = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target) return;
    // Only react if the loaded section actually contains this cart-items component.
    if (!target.contains(this) && target !== this.closest(`#shopify-section-${this.sectionId}`)) return;
    this.#toggleWishlistButtonClass();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#reinitSearchspring();

        setTimeout(() => {
          this.#toggleWishlistButtonClass();
        }, 100);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Find the quantity-dropdown for this line by data-line-index
    const dropdown = this.querySelector(`.quantity-dropdown[data-line-index="${line - 1}"]`);

    if (dropdown) {
      const hiddenInput = dropdown.querySelector('.quantity-dropdown__input');
      const triggerText = dropdown.querySelector('.quantity-dropdown__text');

      // Revert the hidden input to its original value
      if (hiddenInput instanceof HTMLInputElement) {
        const previousValue = hiddenInput.defaultValue;
        hiddenInput.value = previousValue;

        // Revert the visual dropdown text
        if (triggerText) {
          triggerText.textContent = previousValue;
        }

        // Re-select the correct option in the dropdown menu
        const options = dropdown.querySelectorAll('.quantity-dropdown__option');
        options.forEach((opt) => {
          const isSelected = opt.getAttribute('data-value') === previousValue;
          opt.classList.toggle('quantity-dropdown__option--selected', isSelected);
          opt.setAttribute('aria-selected', String(isSelected));
        });
      }
    }

    // Show the error message
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('!hidden');
    } else {
      console.error('[cart] Could not display error:', parsedResponseText.errors);
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#reinitSearchspring();
        this.#toggleWishlistButtonClass();
      });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#reinitSearchspring();
      setTimeout(() => {
        this.#toggleWishlistButtonClass();
      }, 100);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#reinitSearchspring();
        this.#toggleWishlistButtonClass();
      });
    }
  };

  /**
   * Re-initialises all <searchspring-recommendations> elements inside the
   * section after a morph wipes their runtime-rendered products.
   */
  #reinitSearchspring() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!section) return;

    /** @type {NodeListOf<HTMLElement & { setSettings?: () => void; getRecommendations?: () => void }>} */
    const ssElements = section.querySelectorAll('searchspring-recommendations');
    ssElements.forEach((el) => {
      if (typeof el.setSettings === 'function') el.setSettings();
      if (typeof el.getRecommendations === 'function') el.getRecommendations();
    });
  }

  #toggleWishlistButtonClass() {
    const wishlistButtons = this.querySelectorAll('.cart-items__save-for-later');
    if (wishlistButtons.length === 0) return;

    const originalValues = new Map();
    wishlistButtons.forEach(button => {
      const originalValue = button.getAttribute('data-product-handle');
      if (originalValue) {
        originalValues.set(button, originalValue);
        button.removeAttribute('data-product-handle');
      }
    });

    setTimeout(() => {
      wishlistButtons.forEach(button => {
        const originalValue = originalValues.get(button);
        if (originalValue) {
          button.setAttribute('data-product-handle', originalValue);
        }
      });
    }, 100);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
